import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import NavigationHeader from '@/components/NavigationHeader';
import {
  Brain, CheckCircle2, Clock, ArrowRight, ArrowLeft,
  Loader2, AlertCircle, Target, Sparkles, Trophy,
  BookOpen, Calculator, Lightbulb, Shapes, XCircle, Home
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Subject configuration
const SUBJECTS = {
  maths: {
    name: 'Mathematics',
    icon: Calculator,
    color: 'from-blue-600 to-indigo-600',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-600'
  },
  english: {
    name: 'English',
    icon: BookOpen,
    color: 'from-green-600 to-emerald-600',
    bgColor: 'bg-green-500/10',
    textColor: 'text-green-600'
  },
  verbal_reasoning: {
    name: 'Verbal Reasoning',
    icon: Brain,
    color: 'from-purple-600 to-pink-600',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-600'
  },
  non_verbal_reasoning: {
    name: 'Non-Verbal Reasoning',
    icon: Shapes,
    color: 'from-orange-600 to-red-600',
    bgColor: 'bg-orange-500/10',
    textColor: 'text-orange-600'
  },
  science: {
    name: 'Science',
    icon: Lightbulb,
    color: 'from-teal-600 to-cyan-600',
    bgColor: 'bg-teal-500/10',
    textColor: 'text-teal-600'
  }
};

// Year group to Key Stage mapping
const YEAR_TO_KEY_STAGE = {
  'year-1': 'KS1',
  'year-2': 'KS1',
  'year-3': 'KS2',
  'year-4': 'KS2',
  'year-5': 'KS2',
  'year-6': 'KS2',
  'year-7': 'KS3',
  'year-8': 'KS3',
  'year-9': 'KS3',
  'year-10': 'KS4',
  'year-11': 'KS4',
  'year-12': 'A-Level',
  'year-13': 'A-Level',
  '7-plus': '7+',
  '11-plus': '11+',
  'gcse': 'KS4',
  'a-level': 'A-Level'
};

// Year group to subjects mapping
const YEAR_GROUP_SUBJECTS = {
  'KS1': ['maths', 'english'],
  'KS2': ['maths', 'english', 'verbal_reasoning', 'non_verbal_reasoning'],
  'KS3': ['maths', 'english', 'science'],
  'KS4': ['maths', 'english', 'science'],
  '7+': ['maths', 'english'],
  '11+': ['maths', 'english', 'verbal_reasoning', 'non_verbal_reasoning'],
  'A-Level': ['maths', 'english']
};

// Helper function to get Key Stage from year group
const getKeyStageFromYearGroup = (yearGroup) => {
  if (!yearGroup) return 'KS2'; // Default
  const normalized = yearGroup.toLowerCase().replace(/\s+/g, '-');
  return YEAR_TO_KEY_STAGE[normalized] || 'KS2';
};

const UnifiedDiagnostic = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  // Determine user's year group and available subjects
  const userYearGroup = progress?.user?.studentProfile?.yearGroup || 
                        progress?.user?.yearGroup || 
                        progress?.user?.profile?.yearGroup || 
                        null;
  const userKeyStage = getKeyStageFromYearGroup(userYearGroup);
  const availableSubjects = YEAR_GROUP_SUBJECTS[userKeyStage] || YEAR_GROUP_SUBJECTS['KS2'];
  
  // State
  const [phase, setPhase] = useState('select'); // select, intro, testing, complete
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [currentSubjectIndex, setCurrentSubjectIndex] = useState(0);
  const [currentSubject, setCurrentSubject] = useState(null);
  
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [timeStarted, setTimeStarted] = useState(null);
  
  // Results state
  const [subjectResults, setSubjectResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Toggle subject selection
  const toggleSubject = (subject) => {
    setSelectedSubjects(prev => 
      prev.includes(subject)
        ? prev.filter(s => s !== subject)
        : [...prev, subject]
    );
  };

  // Start with selected subjects
  const startWithSelectedSubjects = () => {
    if (selectedSubjects.length === 0) {
      toast.error('Please select at least one subject');
      return;
    }
    setCurrentSubject(selectedSubjects[0]);
    setCurrentSubjectIndex(0);
    setPhase('intro');
  };

  // Start diagnostic for current subject
  const startSubjectDiagnostic = async (subject) => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/ks2-11plus/diagnostic/start`,
        { subject },
        { headers }
      );
      
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
        setCurrentQuestion({
          ...response.data.question,
          question: response.data.question.text
        });
        setTotalQuestions(response.data.total_questions);
        setQuestionIndex(0);
        setTimeStarted(Date.now());
        setSelectedAnswer(null);
        setPhase('testing');
      }
    } catch (err) {
      console.error('Failed to start diagnostic:', err);
      setError('Failed to start diagnostic. Please try again.');
      toast.error('Failed to start diagnostic');
    } finally {
      setLoading(false);
    }
  };

  // Submit answer
  const submitAnswer = async () => {
    if (selectedAnswer === null) {
      toast.error('Please select an answer');
      return;
    }
    
    setLoading(true);
    const timeTaken = Math.round((Date.now() - timeStarted) / 1000);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/ks2-11plus/diagnostic/answer`,
        {
          session_id: sessionId,
          question_index: questionIndex,
          selected_answer: selectedAnswer,
          time_taken: timeTaken
        },
        { headers }
      );
      
      const data = response.data;
      
      if (data.completed) {
        // Subject diagnostic complete
        const results = {
          overall_score: data.results.overall_score,
          mastery_level: data.results.mastery_level,
          mastery_matrix: data.results.mastery_matrix,
          total_correct: data.results.total_correct,
          total_questions: data.results.total_questions,
          question_results: data.results.question_results || []
        };
        
        setSubjectResults(prev => ({
          ...prev,
          [currentSubject]: results
        }));
        
        // Check if there are more subjects
        if (currentSubjectIndex < selectedSubjects.length - 1) {
          // Move to next subject
          const nextIndex = currentSubjectIndex + 1;
          setCurrentSubjectIndex(nextIndex);
          setCurrentSubject(selectedSubjects[nextIndex]);
          setSessionId(null);
          setCurrentQuestion(null);
          setQuestionIndex(0);
          setPhase('intro');
          toast.success(`${SUBJECTS[currentSubject].name} complete! Moving to next subject.`);
        } else {
          // All subjects complete
          setPhase('complete');
          toast.success('Diagnostic assessment complete!');
        }
      } else {
        // Move to next question
        setCurrentQuestion({
          ...data.question,
          question: data.question.text
        });
        setQuestionIndex(prev => prev + 1);
        setSelectedAnswer(null);
        setTimeStarted(Date.now());
      }
    } catch (err) {
      console.error('Failed to submit answer:', err);
      toast.error('Failed to submit answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Complete assessment and go to personalized hub
  const completeAssessment = () => {
    // Save results to progress
    updateProgress({
      hasCompletedDiagnostic: true,
      diagnosticResults: subjectResults,
      diagnosticCompletedAt: new Date().toISOString(),
      diagnosticStatus: 'completed'
    });
    
    navigate('/personalized-hub');
  };

  // Exit assessment
  const exitAssessment = () => {
    if (window.confirm('Are you sure you want to exit? Your progress will be saved.')) {
      updateProgress({
        hasSeenWelcome: true,
        diagnosticStatus: 'partial',
        partialResults: subjectResults
      });
      navigate('/explore');
    }
  };

  // Render subject selection phase
  const renderSubjectSelection = () => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="max-w-2xl mx-auto text-center"
      >
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex p-4 rounded-2xl bg-violet-500/20 mb-6">
            <Target className="w-12 h-12 text-violet-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-3">
            Choose Your Subjects
          </h1>
          <p className="text-slate-500">
            Select the subjects you'd like to be assessed on. You can choose one or more subjects.
          </p>
        </div>

        {/* Subject Grid */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          {availableSubjects.map((subject) => {
            const config = SUBJECTS[subject];
            const Icon = config?.icon || Brain;
            const isSelected = selectedSubjects.includes(subject);
            
            return (
              <motion.div
                key={subject}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  data-testid={`subject-card-${subject}`}
                  onClick={() => toggleSubject(subject)}
                  className={`cursor-pointer transition-all ${
                    isSelected
                      ? `bg-gradient-to-br ${config?.color || 'from-violet-600 to-indigo-600'} border-transparent`
                      : 'bg-white/50 border-slate-200 hover:border-slate-200'
                  }`}
                >
                  <CardContent className="p-6">
                    <div className={`inline-flex p-3 rounded-xl mb-4 ${
                      isSelected ? 'bg-slate-100' : config?.bgColor || 'bg-violet-500/20'
                    }`}>
                      <Icon className={`w-8 h-8 ${isSelected ? 'text-slate-900' : config?.textColor || 'text-violet-600'}`} />
                    </div>
                    <h3 className={`text-lg font-semibold ${isSelected ? 'text-slate-900' : 'text-slate-900'}`}>
                      {config?.name || subject}
                    </h3>
                    <p className={`text-sm mt-1 ${isSelected ? 'text-slate-600' : 'text-slate-500'}`}>
                      12-15 adaptive questions
                    </p>
                    
                    {isSelected && (
                      <div className="mt-3">
                        <CheckCircle2 className="w-6 h-6 text-slate-900 mx-auto" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Selected count */}
        <div className="mb-6">
          <p className="text-slate-500">
            {selectedSubjects.length === 0 
              ? 'No subjects selected' 
              : `${selectedSubjects.length} subject${selectedSubjects.length > 1 ? 's' : ''} selected`
            }
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <Button
            data-testid="start-selected-btn"
            onClick={startWithSelectedSubjects}
            disabled={selectedSubjects.length === 0}
            size="lg"
            className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 disabled:opacity-50"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            Start Assessment ({selectedSubjects.length} subject{selectedSubjects.length !== 1 ? 's' : ''})
          </Button>
          
          <Button
            variant="outline"
            onClick={() => {
              setSelectedSubjects(availableSubjects);
            }}
            className="w-full border-slate-200 hover:bg-slate-100"
          >
            Select All Subjects
          </Button>
          
          <Button
            variant="ghost"
            onClick={() => navigate('/explore')}
            className="text-slate-500 hover:text-slate-600"
          >
            Skip and explore instead
          </Button>
        </div>
      </motion.div>
    );
  };

  // Render intro phase
  const renderIntro = () => {
    const subject = SUBJECTS[currentSubject];
    const Icon = subject?.icon || Brain;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="max-w-xl mx-auto text-center"
      >
        {/* Progress indicator */}
        <div className="mb-8">
          <div className="flex justify-center gap-2 mb-4">
            {selectedSubjects.map((s, i) => (
              <div
                key={s}
                className={`w-3 h-3 rounded-full transition-all ${
                  i < currentSubjectIndex 
                    ? 'bg-green-500' 
                    : i === currentSubjectIndex 
                      ? 'bg-violet-500 ring-2 ring-violet-500/50' 
                      : 'bg-slate-100'
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-slate-500">
            Subject {currentSubjectIndex + 1} of {selectedSubjects.length}
          </p>
        </div>

        <Card className="bg-white/50 border-slate-200">
          <CardContent className="p-8">
            <div className={`inline-flex p-4 rounded-2xl ${subject?.bgColor || 'bg-violet-500/20'} mb-6`}>
              <Icon className={`w-12 h-12 ${subject?.textColor || 'text-violet-600'}`} />
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {subject?.name || 'Subject'} Assessment
            </h2>
            
            <p className="text-slate-500 mb-6">
              Answer questions to help us understand your current level in {subject?.name || 'this subject'}.
              Take your time and answer as best you can.
            </p>

            <div className="flex items-center justify-center gap-6 mb-8 text-sm">
              <div className="flex items-center gap-2 text-slate-500">
                <Clock className="w-4 h-4" />
                <span>~20-25 minutes</span>
              </div>
              <div className="flex items-center gap-2 text-slate-500">
                <Target className="w-4 h-4" />
                <span>Adaptive questions</span>
              </div>
            </div>

            <Button
              data-testid={`start-${currentSubject}-btn`}
              onClick={() => startSubjectDiagnostic(currentSubject)}
              disabled={loading}
              size="lg"
              className={`w-full bg-gradient-to-r ${subject?.color || 'from-violet-600 to-indigo-600'}`}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-5 h-5 mr-2" />
              )}
              Begin {subject?.name || 'Assessment'}
            </Button>
          </CardContent>
        </Card>

        {/* Skip option */}
        <Button
          variant="ghost"
          onClick={exitAssessment}
          className="mt-4 text-slate-500 hover:text-slate-600"
        >
          Exit and explore instead
        </Button>
      </motion.div>
    );
  };

  // Render testing phase
  const renderTesting = () => {
    if (!currentQuestion) return null;
    
    const subject = SUBJECTS[currentSubject];
    const Icon = subject?.icon || Brain;
    const progressPercent = ((questionIndex + 1) / totalQuestions) * 100;
    
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-3xl mx-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={exitAssessment}
            className="text-slate-500 hover:text-slate-900"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Exit
          </Button>
          
          <Badge className={`${subject?.bgColor || 'bg-violet-500/20'} ${subject?.textColor || 'text-violet-600'}`}>
            <Icon className="w-3 h-3 mr-1" />
            {subject?.name || 'Subject'}
          </Badge>
        </div>

        {/* Progress */}
        <Card className="bg-white/50 border-slate-200 mb-6">
          <CardContent className="p-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-500">Question {questionIndex + 1} of {totalQuestions}</span>
              <span className="text-slate-900 font-medium">{Math.round(progressPercent)}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </CardContent>
        </Card>

        {/* Question Card */}
        <motion.div
          key={currentQuestion.q_id || questionIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Card className="bg-white/50 border-slate-200">
            <CardHeader>
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${subject?.bgColor || 'bg-violet-500/20'} shrink-0`}>
                  <span className={`text-lg font-bold ${subject?.textColor || 'text-violet-600'}`}>
                    Q{questionIndex + 1}
                  </span>
                </div>
                <CardTitle className="text-xl text-slate-900 leading-relaxed">
                  {currentQuestion.question || currentQuestion.text}
                </CardTitle>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <RadioGroup
                value={selectedAnswer?.toString()}
                onValueChange={(val) => setSelectedAnswer(parseInt(val))}
                className="space-y-3"
              >
                {currentQuestion.options?.map((option, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Label
                      data-testid={`option-${idx}`}
                      htmlFor={`opt-${idx}`}
                      className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                        selectedAnswer === idx
                          ? 'border-violet-500 bg-violet-500/10'
                          : 'border-slate-200 hover:border-slate-200 bg-slate-100/30'
                      }`}
                    >
                      <RadioGroupItem value={idx.toString()} id={`opt-${idx}`} />
                      <span className="text-lg font-medium text-slate-500 mr-2">
                        {String.fromCharCode(65 + idx)}.
                      </span>
                      <span className="text-slate-900">{option}</span>
                    </Label>
                  </motion.div>
                ))}
              </RadioGroup>

              <div className="pt-4 border-t border-slate-200">
                <Button
                  data-testid="submit-answer-btn"
                  onClick={submitAnswer}
                  disabled={selectedAnswer === null || loading}
                  className={`w-full bg-gradient-to-r ${subject?.color || 'from-violet-600 to-indigo-600'}`}
                  size="lg"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : questionIndex === totalQuestions - 1 ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Complete {subject?.name || 'Subject'}
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-5 h-5 mr-2" />
                      Next Question
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    );
  };

  // Render complete phase
  const renderComplete = () => {
    const totalScore = Object.values(subjectResults).reduce(
      (acc, r) => acc + (r.overall_score || 0), 0
    ) / Object.keys(subjectResults).length;
    
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl mx-auto text-center"
      >
        {/* Celebration */}
        <div className="mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="inline-flex p-6 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 mb-6"
          >
            <Trophy className="w-16 h-16 text-amber-600" />
          </motion.div>
          
          <h1 className="text-3xl font-bold text-slate-900 mb-3">
            Assessment Complete!
          </h1>
          
          <p className="text-slate-500 text-lg">
            Great job! Here's your diagnostic summary.
          </p>
        </div>

        {/* Overall Score */}
        <Card className="bg-white/50 border-slate-200 mb-6">
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-5xl font-bold text-slate-900 mb-2">
                {Math.round(totalScore)}%
              </div>
              <p className="text-slate-500">Overall Score</p>
            </div>
          </CardContent>
        </Card>

        {/* Subject Results */}
        <div className="grid gap-4 mb-8">
          {Object.entries(subjectResults).map(([subject, result]) => {
            const subjectConfig = SUBJECTS[subject];
            const Icon = subjectConfig?.icon || Brain;
            
            return (
              <Card key={subject} className="bg-white/50 border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${subjectConfig?.bgColor || 'bg-violet-500/20'}`}>
                        <Icon className={`w-5 h-5 ${subjectConfig?.textColor || 'text-violet-600'}`} />
                      </div>
                      <div className="text-left">
                        <p className="text-slate-900 font-medium">{subjectConfig?.name || subject}</p>
                        <p className="text-sm text-slate-500 capitalize">{result.mastery_level} level</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-900">{Math.round(result.overall_score)}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Wrong Answers Review Section */}
        {Object.entries(subjectResults).some(([_, result]) => 
          result.question_results?.filter(r => !r.is_correct).length > 0
        ) && (
          <Card className="bg-white/50 border-red-500/30 border-2 mb-8 text-left" data-testid="wrong-answers-review">
            <CardHeader className="bg-red-900/20">
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                Review Wrong Answers
              </CardTitle>
              <p className="text-slate-500 text-sm">Learn from your mistakes to improve</p>
            </CardHeader>
            <CardContent className="space-y-6 pt-4">
              {Object.entries(subjectResults).map(([subject, result]) => {
                const wrongAnswers = result.question_results?.filter(r => !r.is_correct) || [];
                if (wrongAnswers.length === 0) return null;
                
                const subjectConfig = SUBJECTS[subject];
                
                return (
                  <div key={subject} className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                      <Badge className={`${subjectConfig?.bgColor || 'bg-violet-500/20'} ${subjectConfig?.textColor || 'text-violet-600'}`}>
                        {subjectConfig?.name || subject}
                      </Badge>
                      <span className="text-red-600 text-sm">
                        {wrongAnswers.length} wrong answer{wrongAnswers.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    {wrongAnswers.map((result, idx) => (
                      <div 
                        key={result.question_id || idx}
                        className="p-4 bg-slate-100 rounded-lg border border-red-500/20"
                        data-testid={`wrong-answer-${subject}-${idx}`}
                      >
                        {/* Question Number & Domain */}
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className="border-red-500/50 text-red-600">
                            Q{idx + 1}
                          </Badge>
                          {result.domain && (
                            <Badge variant="outline" className="border-slate-300 text-slate-500 capitalize">
                              {result.domain.replace(/_/g, ' ')}
                            </Badge>
                          )}
                        </div>

                        {/* Question Text */}
                        <p className="text-slate-900 font-medium mb-4">
                          {result.question_text || 'Question not available'}
                        </p>

                        {/* Options */}
                        {result.options && result.options.length > 0 && (
                          <div className="grid gap-2 mb-4">
                            {result.options.map((option, optIdx) => {
                              const isUserAnswer = optIdx === result.user_answer;
                              const isCorrectAnswer = optIdx === result.correct_answer;
                              
                              return (
                                <div
                                  key={optIdx}
                                  className={`p-3 rounded-lg border flex items-center gap-3 ${
                                    isCorrectAnswer 
                                      ? 'bg-green-900/30 border-green-500/50' 
                                      : isUserAnswer 
                                        ? 'bg-red-900/30 border-red-500/50'
                                        : 'bg-slate-100 border-slate-200/50'
                                  }`}
                                >
                                  <span className={`font-bold ${
                                    isCorrectAnswer ? 'text-green-600' : 
                                    isUserAnswer ? 'text-red-600' : 'text-slate-500'
                                  }`}>
                                    {String.fromCharCode(65 + optIdx)}.
                                  </span>
                                  <span className={
                                    isCorrectAnswer ? 'text-green-600' : 
                                    isUserAnswer ? 'text-red-600' : 'text-slate-600'
                                  }>
                                    {option}
                                  </span>
                                  {isCorrectAnswer && (
                                    <CheckCircle2 className="h-4 w-4 text-green-600 ml-auto" />
                                  )}
                                  {isUserAnswer && !isCorrectAnswer && (
                                    <XCircle className="h-4 w-4 text-red-600 ml-auto" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Explanation */}
                        {result.explanation && (
                          <div className="p-3 bg-blue-900/20 rounded-lg border border-blue-500/30">
                            <div className="flex items-center gap-2 mb-2">
                              <Lightbulb className="h-4 w-4 text-yellow-400" />
                              <span className="text-yellow-400 font-medium text-sm">Explanation</span>
                            </div>
                            <p className="text-slate-600 text-sm">{result.explanation}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <Button
          data-testid="go-to-hub-btn"
          onClick={completeAssessment}
          size="lg"
          className="w-full bg-gradient-to-r from-violet-600 to-indigo-600"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          View My Personalised Learning Plan
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </motion.div>
    );
  };

  return (
    <div 
      data-testid="unified-diagnostic"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white py-8 px-4"
    >
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      {/* Navigation - Fixed at top */}
      <div className="relative z-20 max-w-4xl mx-auto mb-4">
        <NavigationHeader 
          showBack={true}
          showHome={true}
          homeRoute="/welcome"
          variant="dark"
        />
      </div>

      <div className="relative z-10">
        <AnimatePresence mode="wait">
          {phase === 'select' && renderSubjectSelection()}
          {phase === 'intro' && renderIntro()}
          {phase === 'testing' && renderTesting()}
          {phase === 'complete' && renderComplete()}
        </AnimatePresence>
      </div>

      {/* Error display */}
      {error && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-red-500/10 border border-red-500/30 text-red-600 px-4 py-2 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
    </div>
  );
};

export default UnifiedDiagnostic;
